import type * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { GroupBase, OptionsOrGroups, SingleValue } from 'react-select';

export type CellMeta = {
  formula?: string;
  isHeader?: boolean;
  isItalic?: boolean;
  allowEdit?: boolean;
  isNegative?: boolean;
  isBoldTitle?: boolean;
  isAccordion?: boolean;
  isSeparator?: boolean;
  allowDecimal?: boolean;
  allowNegative?: boolean;
  isIndentedTitle?: boolean;
  accordionChildrenCount?: number;
};

type Quarter = 'q1' | 'q2' | 'q3' | 'q4';

export type QuarterKey = `${Quarter}_${string}`;

export type RowWithDate = {
  QUARTER: string;
  PERIOD_END: string;
  [key: string]: string | number | null | undefined;
};

export type RowWithId = {
  id: string;
  [key: string]: string | number | null | undefined;
};

export type PortCoOverridesResponse = {
  data: RowWithDate[];
  data_is_reviewed: boolean;
};

export interface OverriddenData {
  submission_timestamp: string;
  overrides: RowWithDate[];
}

export type Row = {
  id: TableKeys | ExpansionKeys | HeaderKeys | SeparatorKeys;
  title: string;
  titleExpanded?: string;
  meta?: CellMeta;
} & { [key in QuarterKey]?: string | null };

export type GetColumns = (title: string, labels: string[]) => ColumnDef<Row>[];

export type Option = SingleValue<{ label: string; value: string }>;

export type PortCoUserDataProps = {
  data: Row[];
  isDarkMode: boolean;
  isReviewed: boolean;
  isFormDirty: boolean;
  loadingData: boolean;
  loadingHistory: boolean;
  selectedVersion?: Option;
  isAboutModalOpen: boolean;
  isErrorModalOpen: boolean;
  initialData: RowWithDate[];
  isSubmitModalOpen: boolean;
  loadingSubmission: boolean;
  handleNavigate: () => void;
  handleResetData: () => void;
  toggleAboutModal: () => void;
  handleSubmit: () => Promise<void>;
  handleCloseErrorModal: () => void;
  handleUpdateData: () => Promise<void>;
  handleSelectedVersion: (version: Option) => void;
  historyOptions?: OptionsOrGroups<Option, GroupBase<Option>>;
  renderSection: (title: string, data: Row[], colIndex: number) => React.JSX.Element;
};

export type PriorValues = Partial<Record<TableKeys, number | null>> & {
  last_year_ending_arr?: number | null;
};

export enum TableKeys {
  // KEY FINANCIAL SECTIONS
  BOP_ARR = 'bop_arr',
  NEW_LOGO_ARR = 'new_logo_arr',
  EXPANSION_ARR = 'expansion_arr',
  GROSS_NEW_ARR = 'gross_new_arr',
  GROSS_CHURN_ARR = 'gross_churn_arr',
  NET_NEW_ARR = 'net_new_arr',
  ENDING_ARR = 'ending_arr',
  REVENUE = 'revenue',
  ANNUALIZED_REVENUE = 'annualized_revenue',
  TOTAL_GROSS_PROFIT = 'total_gross_profit',
  GROSS_PROFIT_MARGIN = 'gross_profit_margin',
  TOTAL_OPEX_SM = 'total_opex_sm',
  TOTAL_OPEX_RD = 'total_opex_rd',
  TOTAL_OPEX_GA = 'total_opex_ga',
  TOTAL_OPEX = 'total_opex',
  FCF = 'fcf',
  FCF_MARGIN = 'fcf_margin',
  TOTAL_CUSTOMERS = 'total_customers',
  GROSS_NEW_CUSTOMERS_IN_PERIOD = 'gross_new_customers_in_period',
  ARR_PER_CUSTOMER = 'arr_per_customer',
  TOTAL_HEADCOUNT_SM = 'total_headcount_sm',
  TOTAL_HEADCOUNT_RD = 'total_headcount_rd',
  TOTAL_HEADCOUNT_GA = 'total_headcount_ga',
  TOTAL_HEADCOUNT = 'total_headcount',
  TOTAL_HEADCOUNT_OTHER = 'total_headcount_other',

  // GROWTH AND RETENTION
  YOY_ARR_GROWTH = 'yoy_arr_growth',
  COMPOSITION_NEW_LOGO = 'composition_new_logo',
  COMPOSITION_EXPANSION = 'composition_expansion',
  NEW_LOGO_VELOCITY = 'new_logo_velocity',
  QUICK_RATIO = 'quick_ratio',
  GROSS_DOLLAR_RETENTION = 'gross_dollar_retention',
  NET_DOLLAR_RETENTION = 'net_dollar_retention',

  // OPERATIONAL EFFICIENCY
  NET_MAGIC_NUMBER = 'net_magic_number',
  RULE_OF_40 = 'rule_of_40',
  CAC = 'cac',
  CAC_PAYBACK_PERIOD = 'cac_payback_period',
  BURN_MULTIPLE = 'burn_multiple',
  PERC_OPEX_SM = 'perc_opex_sm',
  PERC_OPEX_RD = 'perc_opex_rd',
  PERC_OPEX_GA = 'perc_opex_ga',

  // PROFITABILITY AND CASH FLOW
  GROSS_PROFIT_MARGIN_1 = 'gross_profit_margin_1',
  FCF_MARGIN_1 = 'fcf_margin_1',

  // HEADCOUNT
  ARR_PER_FTE = 'arr_per_fte',
  OPEX_PER_FTE = 'opex_per_fte',
  PERC_HEADCOUNT_SM = 'perc_headcount_sm',
  PERC_HEADCOUNT_RD = 'perc_headcount_rd',
  PERC_HEADCOUNT_GA = 'perc_headcount_ga',
  PERC_HEADCOUNT_OTHER = 'perc_headcount_other'
}

export enum PortCoKeys {
  ARR_BOP = 'ARR_BOP',
  EXPANSION_ARR_FUNNEL = 'EXPANSION_ARR_FUNNEL',
  ARR_NEWLOGO = 'ARR_NEWLOGO',
  ARR_EXPANSION = 'ARR_EXPANSION',
  ARR_GROSSNEW = 'ARR_GROSSNEW',
  ARR_CHURN_GROSS = 'ARR_CHURN_GROSS',
  ARR_NETNEW = 'ARR_NETNEW',
  ARR_EOP = 'ARR_EOP',
  REVENUE_TOTAL = 'REVENUE_TOTAL',
  ANNUALIZED_REVENUE = 'ANNUALIZED_REVENUE', // TODO: Check field name when fields are returned from BE
  SEPARATOR_1 = 'SEPARATOR_1',
  GROSSPROFIT_TOTAL = 'GROSSPROFIT_TOTAL',
  MARGIN_GROSSPROFIT = 'MARGIN_GROSSPROFIT',
  HEADER_OPEX_DETAILS = 'HEADER_OPEX_DETAILS',
  OPEX_SM = 'OPEX_SM',
  OPEX_RD = 'OPEX_RD',
  OPEX_GA = 'OPEX_GA',
  OPEX_TOTAL = 'OPEX_TOTAL',
  SEPARATOR_2 = 'SEPARATOR_2',
  FCF = 'FCF',
  MARGIN_FCF = 'MARGIN_FCF',
  SEPARATOR_3 = 'SEPARATOR_3',
  CUSTOMERS_EOP = 'CUSTOMERS_EOP',
  CUSTOMERS_GROSSNEW = 'CUSTOMERS_GROSSNEW',
  ARR_PER_CUSTOMER = 'ARR_PER_CUSTOMER',
  SEPARATOR_4 = 'SEPARATOR_4',
  HEADER_HEADCOUNT_DETAILS = 'HEADER_HEADCOUNT_DETAILS',
  EMPLOYEES_SM = 'EMPLOYEES_SM',
  EMPLOYEES_RD = 'EMPLOYEES_RD',
  EMPLOYEES_GA = 'EMPLOYEES_GA',
  EMPLOYEES_TOTAL = 'EMPLOYEES_TOTAL',
  EMPLOYEES_OTHER = 'EMPLOYEES_OTHER',
  YOY_ARR_GROWTH = 'YOY_ARR_GROWTH', // TODO: Check field name when fields are returned from BE
  HEADER_GROSS_NEW_ARR_COMPOSITION = 'HEADER_GROSS_NEW_ARR_COMPOSITION',
  ARR_PERCTOTAL_GROSSNEW_NEWLOGO = 'ARR_PERCTOTAL_GROSSNEW_NEWLOGO',
  ARR_PERCTOTAL_GROSSNEW_EXPANSION = 'ARR_PERCTOTAL_GROSSNEW_EXPANSION',
  NEW_LOGO_VELOCITY = 'NEW_LOGO_VELOCITY',
  QUICK_RATIO_ARR = 'QUICK_RATIO_ARR',
  RETENTION_GROSS_ARR_ANNUALIZED = 'RETENTION_GROSS_ARR_ANNUALIZED',
  RETENTION_NET_ARR_ANNUALIZED = 'RETENTION_NET_ARR_ANNUALIZED',
  MAGICNUMBER_NET = 'MAGICNUMBER_NET',
  RULE_OF_40 = 'RULE_OF_40',
  CAC = 'CAC',
  CAC_PAYBACK = 'CAC_PAYBACK',
  BURN_MULTIPLE = 'BURN_MULTIPLE',
  HEADER_OPEX_AS_PERC_OF_REVENUE = 'HEADER_OPEX_AS_PERC_OF_REVENUE',
  MARGIN_OPEX_SM = 'MARGIN_OPEX_SM',
  MARGIN_OPEX_RD = 'MARGIN_OPEX_RD',
  MARGIN_OPEX_GA = 'MARGIN_OPEX_GA',
  'MARGIN_GROSSPROFIT.1' = 'MARGIN_GROSSPROFIT.1',
  'MARGIN_FCF.1' = 'MARGIN_FCF.1',
  ARR_PER_FTE = 'ARR_PER_FTE',
  OPEX_PER_FTE = 'OPEX_PER_FTE',
  HEADER_HEADCOUNT_DISTRIBUTION = 'HEADER_HEADCOUNT_DISTRIBUTION',
  EMPLOYEES_PERCTOTAL_SM = 'EMPLOYEES_PERCTOTAL_SM',
  EMPLOYEES_PERCTOTAL_RD = 'EMPLOYEES_PERCTOTAL_RD',
  EMPLOYEES_PERCTOTAL_GA = 'EMPLOYEES_PERCTOTAL_GA',
  EMPLOYEES_PERCTOTAL_OTHER = 'EMPLOYEES_PERCTOTAL_OTHER'
}

export enum ExpansionKeys {
  EXPANSION_ARR_FUNNEL = 'expansion_arr_funnel'
}

export enum HeaderKeys {
  HEADER_OPEX_DETAILS = 'header_opex_details',
  HEADER_HEADCOUNT_DETAILS = 'header_headcount_details',
  HEADER_GROSS_NEW_ARR_COMPOSITION = 'header_gross_new_arr_composition',
  HEADER_OPEX_AS_PERC_OF_REVENUE = 'header_opex_as_perc_of_revenue',
  HEADER_HEADCOUNT_DISTRIBUTION = 'header_headcount_distribution'
}

export enum SeparatorKeys {
  SEPARATOR_1 = 'separator_1',
  SEPARATOR_2 = 'separator_2',
  SEPARATOR_3 = 'separator_3',
  SEPARATOR_4 = 'separator_4'
}
